import { useEffect, useMemo } from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, styled } from '@mui/material';

import { setCurrentAnalyticsPage } from '../Slice/analyticsSlice';

import { useAppDispatch, useAppSelector } from 'slices';
import { AnaliticsTableType } from 'components/AnalyticsPage/AnalyticsPageTypes';
import './AnaliticsTitle.scss';
import MultiCatchMouseTrapIcon from 'components/Icons/MultiCatchMouseTrapIcon';
import SmartBaitStationIcon from 'components/Icons/SmartRodentBaitModuleIcon';
import SnapTrapRatIcon from 'components/Icons/SnapTrapRatIcon';
import { ObjectTraps } from 'api/analitics/types';

const CustomSelect = styled(Select)({
  '&.MuiInputBase-root': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderColor: 'red',
  },
  '& .MuiSelect-select': {
    padding: '10px 12px',
    paddingRight: '24px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #E0E0E0',
  },
});

interface AnaliticsTitleProps {
  objectTraps: ObjectTraps;
}

const AnaliticsTitle: React.FC<AnaliticsTitleProps> = ({ objectTraps }) => {
  const { currentAnalyticsPage } = useAppSelector(state => state.Analitics);
  const dispatch = useAppDispatch();
  const pages = useMemo(
    () => [
      ...(objectTraps.mct.was_ever_installed || objectTraps.smst.was_ever_installed
        ? [AnaliticsTableType.MouseTrapsAnalytics]
        : []),
      ...(objectTraps.srst.was_ever_installed ? [AnaliticsTableType.RatTrapsAnalytics] : []),
      ...(objectTraps.srbm.was_ever_installed ? [AnaliticsTableType.SmartRodentBaitModule] : []),
      AnaliticsTableType.Vulnerabilities,
      AnaliticsTableType.Sights,
    ],
    [objectTraps],
  );

  useEffect(() => {
    dispatch(setCurrentAnalyticsPage(pages[0] as AnaliticsTableType));
  }, [objectTraps, dispatch, pages]);

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    dispatch(setCurrentAnalyticsPage(event.target.value as AnaliticsTableType));
  };

  const imageForCurrentTable = (currentAnalyticsPage: AnaliticsTableType) => {
    // Відповідно до поточної таблиці виводимо відповідну іконку девайсу
    switch (currentAnalyticsPage) {
      case AnaliticsTableType.MouseTrapsAnalytics:
        return <MultiCatchMouseTrapIcon />;
      case AnaliticsTableType.RatTrapsAnalytics:
        return <SnapTrapRatIcon />;
      case AnaliticsTableType.SmartRodentBaitModule:
        return <SmartBaitStationIcon />;
      default:
        return <></>;
    }
  };

  return (
    <div className="table-title bg-white border-bottom">
      <div className="title">
        <div className="mx-2">{imageForCurrentTable(currentAnalyticsPage)}</div>
        <div className="d-flex align-items-center h4 mb-0 fw-bold gap-2 ">{currentAnalyticsPage}</div>
      </div>
      <div className="buttons">
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="demo-simple-select-standard-label" sx={{ fontFamily: 'Poppins, sans-serif' }}>
            View Stats for
          </InputLabel>
          <CustomSelect
            value={currentAnalyticsPage}
            onChange={handleChange}
            id="demo-simple-select-filled"
            label="View stats for"
            sx={{
              width: 250,
              fontFamily: 'Poppins, sans-serif',
              '& .MuiSelect-select': {
                fontFamily: 'Poppins, sans-serif',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--vz-purple)',
              },

              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--vz-purple)',
              },
            }}
          >
            {pages.map(pageName => (
              <MenuItem key={pageName} value={pageName} sx={{ fontFamily: 'Poppins, sans-serif' }}>
                {pageName}
              </MenuItem>
            ))}
          </CustomSelect>
        </FormControl>
      </div>
    </div>
  );
};

export default AnaliticsTitle;
